import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import OtherSvgArrow from "../svg/other/OtherSvgArrow";
import { Link, useLocation } from "react-router-dom";

const getItemActiveClass = (item, pathname) => {
    if (item?.startsWith) {
        return pathname.startsWith(item.startsWith) ? "active" : "";
    }
    if (item?.to) {
        return pathname === item.to ? "active" : "";
    }
    return "";
};

const isItemActive = (item, pathname) => {
    if (Array.isArray(item?.children) && item.children.length) {
        return item.children.some((child) => isItemActive(child, pathname));
    }
    if (item?.startsWith) return pathname.startsWith(item.startsWith);
    if (item?.to) return pathname === item.to;
    return false;
};

const SubMenuLink = ({ item, pathname }) => {
    const activeClass = getItemActiveClass(item, pathname);
    const linkClassName = [
        "pc-link",
        "hide-sidebar",
        !activeClass && item?.color ? `text-${item.color}` : "",
        activeClass,
        item?.className || "",
        item?.onClick ? "submenu-link-action" : "",
    ]
        .filter(Boolean)
        .join(" ");

    if (item?.onClick) {
        return (
            <a
                className={linkClassName}
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    item.onClick();
                }}
            >
                {item.icon ? <span className="submenu-item-icon">{item.icon}</span> : null}
                <span className="pc-mtext">{item.label}</span>
            </a>
        );
    }

    if (item?.to !== "" && item?.to) {
        return (
            <Link className={linkClassName} to={item.to}>
                {item.icon ? <span className="submenu-item-icon">{item.icon}</span> : null}
                <span className="pc-mtext">{item.label}</span>
            </Link>
        );
    }

    return (
        <a className={linkClassName} href={item?.href || ""} rel="noopener noreferrer" target="_blank">
            {item.icon ? <span className="submenu-item-icon">{item.icon}</span> : null}
            <span className="pc-mtext">{item.label}</span>
        </a>
    );
};

const SideBarSubItem = ({ item, pathname, index }) => {
    const hasChildren = Array.isArray(item?.children);
    const [show, setShow] = useState(isItemActive(item, pathname));

    if (hasChildren) {
        return (
            <li
                className={`pc-item pc-hasmenu ${show ? "pc-trigger" : ""} ${isItemActive(item, pathname) ? "active" : ""}`}
                key={`${item.label}-${index}`}
            >
                <a
                    className="pc-link"
                    onClick={(e) => {
                        e.preventDefault();
                        setShow(!show);
                    }}
                >
                    {item.icon ? <span className="submenu-item-icon">{item.icon}</span> : null}
                    <span className="pc-mtext">{item.label}</span>
                    <span className="pc-arrow">
                        <OtherSvgArrow />
                    </span>
                </a>

                <AnimatePresence initial={false}>
                    {show && (
                        <motion.ul
                            className="submenu-list submenu-list--nested"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            style={{ overflow: "hidden" }}
                        >
                            {item.children.map((child, childIndex) => (
                                <li className="pc-item" key={`${child.to || child?.href || child.label}-${childIndex}`}>
                                    <SubMenuLink item={child} pathname={pathname} />
                                </li>
                            ))}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </li>
        );
    }

    return (
        <li className="pc-item" key={`${item.to || item?.href || item.label}-${index}`}>
            <SubMenuLink item={item} pathname={pathname} />
        </li>
    );
};

const SideBarItemList = (props) => {
    const location = useLocation();
    const { title, icon, iconClass, itemsList } = props;

    const isActive = () => itemsList?.find((item) => isItemActive(item, location.pathname));

    const [show, setShow] = useState(isActive());

    return (
        <li className={`cursor-pointer pc-item pc-hasmenu ${show ? "pc-trigger" : ""} ${isActive() ? "active" : ""}`}>
            <a className="pc-link" onClick={() => setShow(!show)}>
                <span className={`pc-micon${iconClass ? ` ${iconClass}` : ""}`}>{icon}</span>
                <span className="pc-mtext" data-i18n="Layouts">
                    {title}
                </span>
                <span className="pc-arrow">
                    <OtherSvgArrow />
                </span>
            </a>

            <AnimatePresence initial={false}>
                {show && (
                    <motion.ul
                        className="submenu-list"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                    >
                        {itemsList?.map((item, index) => (
                            <SideBarSubItem
                                item={item}
                                pathname={location.pathname}
                                index={index}
                                key={`${item.to || item?.href || item.label}-${index}`}
                            />
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </li>
    );
};

export default SideBarItemList;
